import React, { forwardRef, useCallback, useRef, useState } from 'react';
import { css, keyframes } from '@emotion/react';
import { theme } from '@frontend/theme';
import { AISearchIcon, NakedButton, XIconSmall } from '@frontend/design-system';

const GRADIENT_END = theme.colors.secondary.eggplant40;
const GRADIENT_START = theme.colors.primary50;
const ERROR_COLOR = theme.colors.critical50;

type CallIntelSearchFieldProps = {
  className?: string;
  clearBtnTrackingId?: string;
  trackingId?: string;
  hasValue?: boolean;
  hasError?: boolean;
  isFocused?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  value: string;
};

const shouldShowGradient = ({
  isFocused,
  isHovered,
  hasValue,
}: {
  isFocused?: boolean;
  isHovered?: boolean;
  hasValue?: boolean;
}): boolean => isFocused || (!isHovered && !hasValue);

const getSearchIconColor = ({
  isFocused,
  isHovered,
  hasValue,
  hasError,
}: {
  isFocused?: boolean;
  isHovered?: boolean;
  hasValue?: boolean;
  hasError?: boolean;
}): string | undefined => {
  if (hasError) return ERROR_COLOR;
  return !isFocused && (isHovered || hasValue) ? theme.font.colors.subdued : undefined;
};

export const CallIntelSearchField = forwardRef<HTMLDivElement, CallIntelSearchFieldProps>(
  (
    {
      className,
      clearBtnTrackingId,
      trackingId,
      hasValue,
      hasError,
      isFocused,
      onChange,
      onClear,
      onKeyDown,
      placeholder,
      value,
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleClearClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isFocused) {
          // Search is expanded — keep focus on the input after clearing
          inputRef.current?.focus();
        } else {
          // Search is collapsed — remove focus entirely
          inputRef.current?.blur();
        }
        onClear?.();
      },
      [isFocused, onClear]
    );

    return (
      <div
        className={className}
        css={styles.wrapper({ isFocused, hasError })}
        data-trackingid={trackingId}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        ref={ref}
      >
        <AISearchIcon
          css={styles.aiSearchIcon}
          fillColor={getSearchIconColor({ isFocused, isHovered, hasValue, hasError })}
          showGradient={!hasError && shouldShowGradient({ isFocused, isHovered, hasValue })}
          size={16}
        />
        <input
          autoComplete='off'
          css={styles.input}
          name='transcript-search'
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          ref={inputRef}
          type='text'
          value={value}
        />
        {!!value && (
          <NakedButton
            aria-label='Clear field'
            css={styles.clearButton}
            onClick={handleClearClick}
            trackingId={clearBtnTrackingId}
          >
            <XIconSmall />
          </NakedButton>
        )}
      </div>
    );
  }
);

CallIntelSearchField.displayName = 'CallIntelSearchField';

const rotateAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const styles = {
  aiSearchIcon: css`
    min-height: ${theme.spacing(2)};
    min-width: ${theme.spacing(2)};
  `,

  clearButton: css`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: ${theme.spacing(3)};
    height: ${theme.spacing(3)};
    width: ${theme.spacing(3)};
    padding: ${theme.spacing(0.5)};
    border-radius: ${theme.borderRadius.small};
    color: ${theme.colors.neutral80};
    transition: background-color 250ms ease-in-out;

    &:hover {
      background: ${theme.colors.neutral5};
    }

    svg {
      fill: ${theme.colors.neutral80};
    }
  `,

  input: css`
    flex: 1;
    min-width: 0;
    width: 100%;
    background: transparent;
    border: none;
    color: ${theme.colors.neutral90};
    display: block;
    font-family: ${theme.font.family};
    font-size: ${theme.fontSize(16)};
    line-height: 1.375;
    text-align: left;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    -webkit-appearance: none;

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: ${theme.font.colors.subdued};
    }
  `,

  wrapper: ({ isFocused, hasError }: { isFocused?: boolean; hasError?: boolean }) => {
    const borderWidth = isFocused ? 2 : 1;

    return css`
      display: flex;
      align-items: center;
      gap: ${theme.spacing(1)};
      position: relative;
      border-radius: ${theme.borderRadius.full};
      background: ${theme.colors.white};
      padding: ${theme.spacing(1.25, 2)};
      overflow: hidden;
      border: none;
      cursor: pointer;

      & > * {
        position: relative;
        z-index: 2;
        cursor: pointer;
      }

      ${isFocused ? `box-shadow: ${theme.shadows.light};` : ''}

      &::before {
        content: '';
        position: absolute;
        inset: ${borderWidth}px;
        background: ${theme.colors.white};
        border-radius: inherit;
        z-index: 1;
      }

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: ${hasError ? ERROR_COLOR : `linear-gradient(90deg, ${GRADIENT_START}, ${GRADIENT_END})`};
        border-radius: inherit;
        z-index: 0;
      }

      ${!hasError &&
      !isFocused &&
      css`
        &:hover::after {
          background: conic-gradient(from 0deg, ${GRADIENT_START}, ${GRADIENT_END}, ${GRADIENT_START});
          animation: ${rotateAnimation} 3s linear infinite;
          filter: blur(16px);
          inset: -100px;
        }
      `}
    `;
  },
};
